<template>
	<div> 
		<!-- The teacher view of a student progress report should include the student name, programme, year and group / firm at the top. -->
		<div v-if="student && showheaders" class="clearfix">
			<div class="float-right">
                <div class="form-group" v-if="!hideTargets">
                    <select class="form-control" v-model="selectedTerm" @change="onTermSelected()">
                        <option :value="null">{{$t('targets')}}</option>
                        <template v-for="(term, index) in $config.getValue('schoolterms')">
                        <option
                            :key="index"
                            :value="term"
                            >{{ term.programme_name }} {{ term.schoolterm_title }}
                        </option>
                        </template>
                    </select>
                </div>
				<button v-if="$config.hasModule('clinical') && $UserSession.hasPriv.call(this, 'REPORT_INDIVIDUAL')" class="btn btn-dark ml-2" @click="$router.push(`/reports/activity/individual/${student.id}`)">{{ $t('activity-report') }}</button>
				<button v-if="isTeacher && $UserSession.hasPriv.call(this, 'REPORT_ANALYSIS')" class="btn btn-dark ml-2" @click="$router.push(`/reports/aggregated/progress/${student.programme}/${student.year}`)">{{ $t('analysis-report') }}</button>
			</div>
			<h1>
				{{ student.displayName }}
				<button v-if="isTeacher" class="btn btn-outline-dark btn-sm" @click="selectStudent()">{{ $t('change') }}</button>
			</h1>
			<ul class="list-inline">
				<li class="list-inline-item">{{ $t('programme-colon') }} {{ $config.getProgrammeNameById(student.programme) }}</li>
				<li class="list-inline-item">{{ $t('year-colon') }} {{ $config.getUserYearStr(student) }}</li>
				<li class="list-inline-item">{{ $t('group-colon') }} {{ $config.getGroupNameById(student.group) }}</li>
				<li class="list-inline-item">{{ $t('firm-colon') }} {{ $config.getFirmNameById(student.firm) }}</li>
			</ul>
		</div>
		<div v-for="(row, rowIndex) in reportconfig.grid" v-bind:key="rowIndex" class="row">
			<div v-for="(col, colIndex) in row" :key="colIndex" v-bind:class="getColClass(col)">
				<div v-for="(datapoint, dIndex) in col.fields" v-bind:key="dIndex">

					<h2 v-if="datapoint.type == 'heading'">{{ datapoint.caption }}</h2>

					<div v-if="datapoint.type == 'bargraphhorizontal'">
						<BarGraphHorizontal
							:settings="datapoint" 
							:student="student" 
							:hideTarget="hideTargets"
							:includeTimeSeries="includeTimeSeries"
                            @display-plot="togglePlot"
						/>
                        <!--b-button v-b-toggle.collapse-test 
                            v-if="includeTimeSeries"
                            variant="primary"
                            @click="togglePlot(datapoint.data)"
                            >
                            {{ $t("plot") }}
                        </b-button-->
                        <b-collapse
                            v-model="visiblePlot[datapoint.data]"
                            :id="`collapse-${datapoint.data}`" 
                            class="mt-2"
                        >
                            <b-card>
                                <TimeSeries 
                                    v-if="datapoint.ready && visiblePlot[datapoint.data]"
                                    :parameters="{
                                        //chartTitle: `${datapoint.caption} for ${student.displayName}`,
                                        student: student,
                                        //dataToPlot: datapoint.data.split('.')[0],
                                        //field: datapoint.data.split('.')[1],
                                        target: datapoint.target,
                                        data: datapoint
                                    }"
                                 />
                            </b-card>
                        </b-collapse>
					</div>

					<div v-if="datapoint.type == 'radialgraphclockwise'">
						<RadialGraphClockwise
							:settings="datapoint"
						/>
					</div>


					<div v-if="datapoint.type == 'datepassed'">
						<DatePassed
							:settings="datapoint"
						/>
					</div>

					<div v-if="datapoint.type == 'upperlowerbargraph'">
						<UpperLowerBarGraph
							:data="getUpperLowerData()"
							:types="getUpperLowerTypes()"
						/>
					</div>

					<div v-if="datapoint.type == 'piechart'">
						<PieChart
							type="donut"
							:settings="datapoint"
						/>
					</div>

					<div v-if="datapoint.type == 'textlist'">
						<TextList
							:settings="datapoint"
						/>
					</div>

				</div>
			</div>
		</div>
	</div>
</template>

<script>
import RadialGraphClockwise from "./RadialGraphClockwise";
import BarGraphHorizontal from "./BarGraphHorizontal";
import UpperLowerBarGraph from "./UpperLowerBarGraph";
import DatePassed from "./DatePassed";
import PieChart from "./PieChart";
import TextList from "./TextList";
import TimeSeries from "./TimeSeries";
import { ReportDataElementFetchMixin } from "@/mixins";

export default {
	mixins: [ReportDataElementFetchMixin],
	props: {
		loadedconfig: Object,
		studentid: String,
		selectStudent: null,
		showheaders: {
            type: Boolean,
            default: true,
        },
        hideTargets: {
            type:Boolean,
            default: false
        },
        includeTimeSeries: {
            type:Boolean,
            default: true
        }
	},
	components: {
		RadialGraphClockwise,
		BarGraphHorizontal,
		UpperLowerBarGraph,
		DatePassed,
		PieChart,
		TextList,
		TimeSeries
	},
	data() {
		return {
			reportconfig: {},
			pointers: {},
			graphdata: {},
			student: null,
			chartTitle: 'Plot title',
			dataToPlot: 'treatmentsession',
            visiblePlot:{},
            isLoaded:{},
            selectedTerm: null,
            currentTargetValue: 0,
            nestedTargets: null,
            nestedTargetsBySource: {},
            targetSwitch: true
		};
	},
	mounted() {
		this.getStudentDetails();
        this.nestedTargets = null
		if(this.loadedconfig) {
			this.getReportConfig();
		}
	},
	watch: {
		studentid: function () {
			this.getStudentDetails();
		} ,
		loadedconfig: function () {
			if(this.loadedconfig) {
                this.nestedTargets = null
				this.getReportConfig();
			}
		}
	},
	methods: {
        onShow(data) {
            this.$set(this.isLoaded, data, true);
        },
        togglePlot(caption){
            if(!this.visiblePlot[caption]){ 
                this.visiblePlot[caption] = true;
                return ;
            }
            this.visiblePlot[caption] = !this.visiblePlot[caption]
        },
		getStudentDetails() {
			if(this.studentid){
				this.$users.getUserById.call(this, this.studentid).then(student => {
					this.student = student;
				});
			}
		},
		getColClass(col){
			return 'col-md-' + col.width;
		},
		// getReportConfig(){
		// 	this.pointers = {};
		// 	this.reportdata = {};
		// 	this.graphdata = {};
		// 	if('grid' in this.loadedconfig){
		// 		var loadedconfig = JSON.parse(JSON.stringify(this.loadedconfig));
		// 		if(!('filters' in loadedconfig)){
		// 			loadedconfig.filters = {};
		// 		}
		// 		var datapoint = '';
		// 		var itemkey = '';
		// 		for(var row in loadedconfig.grid){
		// 			for(var col in loadedconfig.grid[row]){
		// 				for(var field in loadedconfig.grid[row][col].fields){
		// 					// Data series (pie charts / bar graphs, etc
		// 					if(!('filters' in loadedconfig.grid[row][col].fields[field])){
		// 						loadedconfig.grid[row][col].fields[field].filters = {};
		// 					}
		// 					if(Array.isArray(loadedconfig.grid[row][col].fields[field].data)){
		// 						loadedconfig.grid[row][col].fields[field].ready = false;
		// 						for(var item in loadedconfig.grid[row][col].fields[field].data){
		// 							loadedconfig.grid[row][col].fields[field].data[item].ready = false;
		// 							loadedconfig.grid[row][col].fields[field].data[item].value = 0;
		// 							loadedconfig.grid[row][col].fields[field].data[item].target = 0;
		// 							loadedconfig.grid[row][col].fields[field].data[item].median = 0;
		// 							if(loadedconfig.grid[row][col].fields[field].data[item].data){
		// 								datapoint = loadedconfig.grid[row][col].fields[field].data[item].data.split('.');
		// 								itemkey = datapoint[0]+JSON.stringify(loadedconfig.grid[row][col].fields[field].filters);
        //                                 loadedconfig.grid[row][col].fields[field]._datasource = datapoint[0];
		// 								if(!(itemkey in this.pointers)){
		// 									this.pointers[itemkey] = {
		// 										"name": datapoint[0],
		// 										"filters": loadedconfig.grid[row][col].fields[field].filters,
		// 										"pointers": []
		// 									};
		// 								}
		// 								if(!(datapoint[0] in this.pointers)){
		// 									this.pointers[datapoint[0]] = {};
		// 								}
		// 								if(!(datapoint[1] in this.pointers[itemkey].pointers)){
		// 									this.pointers[itemkey].pointers[datapoint[1]] = [];
		// 								}
		// 								this.pointers[itemkey].pointers[datapoint[1]].push({
		// 									'row': row,
		// 									'col': col,
		// 									'field': field,
		// 									'item': item
		// 								});
		// 							}
		// 						}
		// 					} else {
		// 						loadedconfig.grid[row][col].fields[field].ready = false;
		// 						if(field == 'items'){
		// 							loadedconfig.grid[row][col].fields[field].value = [];
		// 						} else {
		// 							loadedconfig.grid[row][col].fields[field].value = 0;
		// 						}
		// 						loadedconfig.grid[row][col].fields[field].target = 0;
		// 						loadedconfig.grid[row][col].fields[field].median = 0;
		// 						if(loadedconfig.grid[row][col].fields[field].data){
		// 							datapoint = loadedconfig.grid[row][col].fields[field].data.split('.');
		// 							itemkey = datapoint[0]+JSON.stringify(loadedconfig.grid[row][col].fields[field].filters);
		// 							if(!(itemkey in this.pointers)){
		// 								this.pointers[itemkey] = {
		// 									"name": datapoint[0],
		// 									"filters": loadedconfig.grid[row][col].fields[field].filters,
		// 									"pointers": []
		// 								};
		// 							}
		// 							if(!(datapoint[1] in this.pointers[itemkey].pointers)){
		// 								this.pointers[itemkey].pointers[datapoint[1]] = [];
		// 							}
		// 							this.pointers[itemkey].pointers[datapoint[1]].push({
		// 								'row': row,
		// 								'col': col,
		// 								'field': field
		// 							});
		// 						}
		// 					}
		// 				}
		// 			}
		// 		}
		// 		this.reportconfig = loadedconfig;
        //          this.$nextTick(() => this.debugReportStructure());
		// 		for(var datasource in this.pointers){
		// 			for(var propname in this.pointers[datasource].pointers){
		// 				this.getReportDataValue(datasource, this.pointers[datasource], propname);					}
		// 		};
		// 	}
		// },
		getReportConfig(){
            this.pointers = {};
            this.reportdata = {};
            this.graphdata = {};
            this.nestedTargetsBySource = {}; 
            
            if('grid' in this.loadedconfig){
                var loadedconfig = JSON.parse(JSON.stringify(this.loadedconfig));
                if(!('filters' in loadedconfig)){
                loadedconfig.filters = {};
                }
                
                for(var row in loadedconfig.grid){
                for(var col in loadedconfig.grid[row]){
                    for(var field in loadedconfig.grid[row][col].fields){
                    if(loadedconfig.grid[row][col].fields[field].data){
                        if(typeof loadedconfig.grid[row][col].fields[field].data === 'string'){
                        const datasourceName = loadedconfig.grid[row][col].fields[field].data.split('.')[0];
                        loadedconfig.grid[row][col].fields[field]._datasource = datasourceName;
                        } else if(Array.isArray(loadedconfig.grid[row][col].fields[field].data)){
                        // For array fields, annotate the field itself (not individual items)
                        // We'll use the first item's datasource as representative
                        if(loadedconfig.grid[row][col].fields[field].data.length > 0){
                            const firstItem = loadedconfig.grid[row][col].fields[field].data[0];
                            if(firstItem.data && typeof firstItem.data === 'string'){
                            const datasourceName = firstItem.data.split('.')[0];
                            loadedconfig.grid[row][col].fields[field]._datasource = datasourceName;
                            }
                        }
                        }
                    }
                    
                    if(!('filters' in loadedconfig.grid[row][col].fields[field])){
                        loadedconfig.grid[row][col].fields[field].filters = {};
                    }
                    
                    if(Array.isArray(loadedconfig.grid[row][col].fields[field].data)){
                        loadedconfig.grid[row][col].fields[field].ready = false;
                        for(var item in loadedconfig.grid[row][col].fields[field].data){
                        loadedconfig.grid[row][col].fields[field].data[item].ready = false;
                        loadedconfig.grid[row][col].fields[field].data[item].value = 0;
                        loadedconfig.grid[row][col].fields[field].data[item].target = 0;
                        loadedconfig.grid[row][col].fields[field].data[item].median = 0;
                        
                        if(loadedconfig.grid[row][col].fields[field].data[item].data){
                            var datapoint = loadedconfig.grid[row][col].fields[field].data[item].data.split('.');
                            var itemkey = datapoint[0]+JSON.stringify(loadedconfig.grid[row][col].fields[field].filters);
                            
                            if(!(itemkey in this.pointers)){
                            this.pointers[itemkey] = {
                                "name": datapoint[0],
                                "filters": loadedconfig.grid[row][col].fields[field].filters,
                                "pointers": []
                            };
                            }
                            if(!(datapoint[0] in this.pointers)){
                            this.pointers[datapoint[0]] = {};
                            }
                            if(!(datapoint[1] in this.pointers[itemkey].pointers)){
                            this.pointers[itemkey].pointers[datapoint[1]] = [];
                            }
                            this.pointers[itemkey].pointers[datapoint[1]].push({
                            'row': row,
                            'col': col,
                            'field': field,
                            'item': item
                            });
                        }
                        }
                    } else {
                        loadedconfig.grid[row][col].fields[field].ready = false;
                        if(field == 'items'){
                        loadedconfig.grid[row][col].fields[field].value = [];
                        } else {
                        loadedconfig.grid[row][col].fields[field].value = 0;
                        }
                        loadedconfig.grid[row][col].fields[field].target = 0;
                        loadedconfig.grid[row][col].fields[field].median = 0;
                        loadedconfig.grid[row][col].fields[field].grade = null;
                        loadedconfig.grid[row][col].fields[field].gradepassed = null;
                        loadedconfig.grid[row][col].fields[field].gradedate = null;

                        if(loadedconfig.grid[row][col].fields[field].data){
                        datapoint = loadedconfig.grid[row][col].fields[field].data.split('.');
                        itemkey = datapoint[0]+JSON.stringify(loadedconfig.grid[row][col].fields[field].filters);

                        if(!(itemkey in this.pointers)){
                            this.pointers[itemkey] = {
                            "name": datapoint[0],
                            "filters": loadedconfig.grid[row][col].fields[field].filters,
                            "pointers": []
                            };
                        }
                        if(!(datapoint[1] in this.pointers[itemkey].pointers)){
                            this.pointers[itemkey].pointers[datapoint[1]] = [];
                        }
                        this.pointers[itemkey].pointers[datapoint[1]].push({
                            'row': row,
                            'col': col,
                            'field': field
                        });

                        // datepassed elements optionally show an attained grade
                        // alongside their pass/fail value - if the bound data
                        // element returns a 'grade' property, wire it up too.
                        if(loadedconfig.grid[row][col].fields[field].type == 'datepassed' && datapoint[1] != 'grade'){
                            if(!('grade' in this.pointers[itemkey].pointers)){
                                this.pointers[itemkey].pointers['grade'] = [];
                            }
                            this.pointers[itemkey].pointers['grade'].push({
                                'row': row,
                                'col': col,
                                'field': field
                            });
                        }
                        }
                    }
                    }
                }
                }
                
                this.reportconfig = loadedconfig;
                
                // Fetch data for all pointers. When 'grade' is auto-added
                // alongside another property on the same element (see above),
                // it's delivered by that property's own fetch - since a fetch
                // always returns the full result for an element - so it must
                // not also trigger a fetch of its own, which would otherwise
                // race the primary fetch's retry loop for the same underlying
                // cache key. But if 'grade' is the *only* registered property
                // (a datepassed element bound directly to a grade-only data
                // element, eg. a form's attained grade with no pass/fail
                // value of its own), it has to fetch, since nothing else will.
                for(var datasource in this.pointers){
                	// Array-bound fields also register a bare datasource-name
                	// key (see above) with no 'pointers' of its own - skip it
                	// rather than letting Object.keys(undefined) throw and
                	// abort the rest of this loop.
                	if(!this.pointers[datasource].pointers){
                		continue;
                	}
                	var propnames = Object.keys(this.pointers[datasource].pointers);
                	for(var propname of propnames){
                    	if(propname == 'grade' && propnames.length > 1){
                    		continue;
                    	}
                    	this.getReportDataValue(datasource, this.pointers[datasource], propname);
                	}
                }
            }
        },
        setElementData(data, elementname , name){

			// Flatten targets
			if (data.values) {
				data = this.flattenTargets(data);
			}

			if(elementname in this.pointers){
				for(var property in this.pointers[elementname].pointers){
					if(property == 'items'){
						this.pointers[elementname].pointers[property].forEach((pointer) => {
							if('items' in data.values){
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].value = data.values['items'];
							}
							this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].ready = true;
						});
					} else if(property == 'grade'){
						this.pointers[elementname].pointers[property].forEach((pointer) => {
							if('grade' in data.values){
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].grade = data.values['grade'];
							}
							if('gradepassed' in data.values){
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].gradepassed = data.values['gradepassed'];
							}
							if('gradedate' in data.values){
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].gradedate = data.values['gradedate'];
							}
							// Usually redundant with the companion property's
							// own pass below - but when 'grade' is the only
							// bound property (no pass/fail value to key off),
							// this is the only place 'ready' ever gets set.
							this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].ready = true;
						});
					} else {
						var value = 0;
						var target = 0;
						var median = 0;
						var quartiles = [];
						// if(property in data.values){
						// 	//console.log(property + ': ' + data.values[property]);
						// 	value = data.values[property];
						// }
                        if(property in data.values){
                            value = data.values[property];
                        } else if(data.values?.results?.global && property in data.values.results.global) {
                            // Global datasource — pull directly from the global aggregate
                            value = data.values.results.global[property];
                        }
						if(data.targets){
							if(property in data.targets){
								//console.log(property + ': ' + data.targets[property]);
								target = data.targets[property];
							}
						}
						if(data.stats){
							if(property in data.stats && data.stats[property] !== null){
								//console.log(property + ': ' + data.targets[property]);
								median = data.stats[property].median;
								quartiles[0] = 0;
								quartiles[1] = Number(data.stats[property].q1);
								quartiles[2] = Number(data.stats[property].q2);
								quartiles[3] = Number(data.stats[property].q3);
								quartiles[4] = Number(data.stats[property].q4);
							}
						}
						this.pointers[elementname].pointers[property].forEach((pointer) => {
							if('item' in pointer){
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].ready = true;
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].value = Number(value);
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].target = Number(target);
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].median = median;
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].quartiles = quartiles;
								if('percentage' in this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item]){
									if(this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].percentage){
										this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data[pointer.item].percentage = Number(value);
									}
								}
								var allready = true
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].data.forEach(item => {
									if(!item.ready){
										allready = false;
									}
								});
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].ready = allready;
							} else {
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].ready = true;
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].value = Number(value);
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].target = Number(target);
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].median = median;
								this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].quartiles = quartiles;
								if('percentage' in this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field]){
									if(this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].percentage){
										this.reportconfig.grid[pointer.row][pointer.col].fields[pointer.field].percentage = Number(value);
									}
								}
							}
						});
					}
				}
				this.graphdata[elementname] = {
                    value: data.values[name],
                    stats: (data.stats && data.stats[name] !== null) ? data.stats[name] : null
                };
			}
		},
		getReportDataValue(elementname, datasource , propname){
			const data = {};
			data.filters = Object.assign({}, this.reportconfig.filters, datasource.filters);
			if(this.studentid) {
				data.student_id = this.studentid;
			}
			this.fetchReportDataElement(elementname, datasource.name, data, (result) => {
				if (result?.values?.targets) {
					this.$set(this.nestedTargetsBySource, datasource.name, result.values.targets);
				}
				this.setElementData(result , elementname , propname);
			});
		},
		getUpperLowerTypes() {
			return [
				{
					name: "Composite",
					color: "#10B981"
				},
				{
					name: "Amalgam",
					color: "#3B82F6"
				},
				{
					name: "GIC",
					color: "#F59E0B"
				}
			];
		},
		getUpperLowerData() {
			return [
				{
					name: "Incisors",
					values: [
						[-2, 4],
						[-9, 8],
						[-1, 10]
					]
				},
				{
					name: "Canines",
					values: [
						[-1, 2],
						[-3, 3],
						[-10, 1]
					]
				},
				{
					name: "Premolars",
					values: [
						[-7, 8],
						[-7, 6],
						[-4, 9]
					]
				},
				{
					name: "Molars",
					values: [
						[-5, 2],
						[-6, 1],
						[-9, 5]
					]
				}
			];
		},
        flattenTargets(data) {
            // Flatten values.targets.*.*.*.total -> values.targets_X_Y_Z_total
            // Also supports per-metric leaves: values.targets.*.*.*.metric -> values.targets_X_Y_Z_metric
            if (data.values?.targets) {
                const isLeaf = (obj) => obj && typeof obj === 'object' && Object.values(obj).every(v => typeof v !== 'object');
                const flatten = (obj, prefix = 'targets') => {
                    Object.entries(obj).forEach(([key, value]) => {
                        if (value && typeof value === 'object' && 'total' in value && isLeaf(value)) {
                            // Structure 1: leaf with a single "total" key
                            data.values[`${prefix}_${key}_total`] = Number(value.total);
                        } else if (value && typeof value === 'object' && isLeaf(value)) {
                            // Structure 2: leaf with per-metric keys (e.g. polish, history_taking, ...)
                            Object.entries(value).forEach(([metricKey, metricVal]) => {
                                data.values[`${prefix}_${key}_${metricKey}`] = Number(metricVal);
                            });
                        } else if (value && typeof value === 'object') {
                            // Recurse deeper
                            flatten(value, `${prefix}_${key}`);
                        }
                    });
                };
                flatten(data.values.targets);
                //delete data.values.targets;
            }
            return data;
        },
        getTargetForTerm(term) {
            if (!term || !this.nestedTargets) {
                console.warn('Targets not loaded yet or invalid term');
                return 0;
            }
            
            // Convert IDs to STRINGS (API uses string keys!)
            const progId = String(term.programme_id);
            const yearId = String(term.schoolyear_id);
            const termId = String(term.schoolterm_id);
            
            // Navigate nested structure: targets[programme][year][term]
            const targetNode = this.nestedTargets[progId]?.[yearId]?.[termId];

            if (!targetNode) {
                console.warn(`No target found for P:${progId} Y:${yearId} T:${termId}`);
                return 0;
            }

            // Structure 1: single total value
            if (targetNode.total) return Number(targetNode.total);

            // Structure 2: per-metric values — return the sum of all metrics as the total
            const values = Object.values(targetNode).map(Number).filter(v => !isNaN(v));
            return values.length ? values.reduce((a, b) => a + b, 0) : 0;
        },

        onTermSelected() {
            if (!this.selectedTerm) {
                this.updateReportWithTargetForTerm(null);
                return;
            }
            
            this.updateReportWithTargetForTerm(this.selectedTerm);
        },
        updateReportWithTargetForTerm(term) {
            if (!term || !this.reportconfig?.grid) return;
            
            const progId = String(term.programme_id);
            const yearId = String(term.schoolyear_id);
            const termId = String(term.schoolterm_id);
            
            //let updatedCount = 0;
        
            for (const row of this.reportconfig.grid) {
                for (const col of row) {
                for (const field of col.fields) {
                    // Only update bar graphs that have a datasource annotation
                    if (field.type !== 'bargraphhorizontal' || !field._datasource) continue;
                    
                    const targetsForMetric = this.nestedTargetsBySource[field._datasource];
                    if (!targetsForMetric) {
                    console.warn(`No targets loaded for datasource "${field._datasource}"`);
                    continue;
                    }
                    
                    const targetNode = targetsForMetric[progId]?.[yearId]?.[termId];
                    if (targetNode?.total) {
                        // Structure 1: single total for every metric
                        const targetValue = Number(targetNode.total);
                        field.target = targetValue;
                        if (Array.isArray(field.data)) {
                            field.data.forEach(item => {
                                if (item.target !== undefined) item.target = targetValue;
                            });
                        }
                    } else if (targetNode && typeof targetNode === 'object') {
                        // Structure 2: per-metric targets keyed by metric name
                        if (Array.isArray(field.data)) {
                            field.data.forEach(item => {
                                if (item.data && typeof item.data === 'string') {
                                    const metricName = item.data.split('.')[1];
                                    if (metricName && metricName in targetNode) {
                                        item.target = Number(targetNode[metricName]);
                                    }
                                }
                            });
                        } else if (typeof field.data === 'string') {
                            const metricName = field.data.split('.')[1];
                            if (metricName && metricName in targetNode) {
                                field.target = Number(targetNode[metricName]);
                            }
                        }
                    }
                }
                }
            }
        
        },
//     updateReportWithTarget(targetValue) {
//     if (!this.reportconfig?.grid) return;
    
//     let targetUpdated = false;
    
//     // ONLY update fields specifically showing the OVERALL TOTAL progress
//     for (const row of this.reportconfig.grid) {
//         for (const col of row) {
//         for (const field of col.fields) {
//             // Skip non-bar-graph fields
//             if (field.type !== 'bargraphhorizontal') continue;
            
//             // CASE 1: Single-value field (e.g., "Overall Progress" bar)
//             if (typeof field.data === 'string' && field.data === 'values.total') {
//             field.target = targetValue;
//             targetUpdated = true;
//             console.log(`✅ Set target=${targetValue} for overall total bar`);
//             continue;
//             }
            
//             // CASE 2: Multi-item field (array of metrics like Composite/Amalgam/GIC)
//             // DO NOT update these—they don't have per-metric targets in your API!
//             // Their targets should remain 0 or use static config values
//         }
//         }
//     }
    
//     if (!targetUpdated) {
//         console.warn(
//         '⚠️ No bar graph found with data="values.total". ' +
//         'Check your report config has a field mapped to "values.total" for overall progress.'
//         );
//     }
// },

},
computed: {
    isTeacher() {
        return this.$UserSession.hasPriv.call(this, "MARK_TREATMENTSESSIONS");
    },
},

};
</script>
